import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, ordersAPI } from '../services/api';

const STATUS_OPTIONS = ['pending', 'picked_up', 'processing', 'ready', 'delivered', 'cancelled'];
const STATUS_LABELS = {
  pending: 'Pending',
  picked_up: 'Picked Up',
  processing: 'Processing',
  ready: 'Ready',
  delivered: 'Delivered',
  cancelled: 'Cancelled'
};

const TABS = [
  ['today', "Today's Schedule"],
  ['new-order', '+ New Order'],
  ['orders', 'Orders'],
  ['subscriptions', 'Subscriptions'],
  ['customers', 'Customers'],
  ['pricing', 'Pricing'],
  ['areas', 'Service Areas'],
  ['settings', 'Settings']
];

const money = (n) => `£${Number(n || 0).toFixed(2)}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('today');
  const [banner, setBanner] = useState(null); // { type: 'success'|'error', text }

  const flash = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 4000);
  };

  return (
    <div className="container admin-dashboard">
      <h1 className="section-title" style={{ marginBottom: '0.5rem' }}>Owner Dashboard</h1>

      <div className="admin-tabs">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            className={`btn ${activeTab === key ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {banner && (
        <div className={banner.type === 'success' ? 'success-message' : 'error-message'} style={{ margin: '1rem 0' }}>
          {banner.text}
        </div>
      )}

      {activeTab === 'today' && <TodayTab flash={flash} />}
      {activeTab === 'new-order' && <NewOrderTab flash={flash} onCreated={() => setActiveTab('today')} />}
      {activeTab === 'orders' && <OrdersTab flash={flash} />}
      {activeTab === 'subscriptions' && <SubscriptionsTab />}
      {activeTab === 'customers' && <CustomersTab />}
      {activeTab === 'pricing' && <PricingTab flash={flash} />}
      {activeTab === 'areas' && <AreasTab flash={flash} />}
      {activeTab === 'settings' && <SettingsTab flash={flash} />}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Today's schedule                                                    */
/* ------------------------------------------------------------------ */
function CapacityBar({ label, used, max }) {
  const pct = max > 0 ? Math.min(used / max, 1) : 0;
  const color = pct >= 1 ? 'var(--error-color, #e53e3e)' : pct >= 0.7 ? 'var(--warning-color, #dd6b20)' : 'var(--success-color, #38a169)';
  return (
    <div style={{ flex: 1, minWidth: '180px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
        <span>{label}</span>
        <strong>{used} / {max}{pct >= 1 ? ' — FULL' : ''}</strong>
      </div>
      <div style={{ background: '#e2e8f0', borderRadius: '6px', height: '10px', overflow: 'hidden' }}>
        <div style={{ width: `${pct * 100}%`, height: '100%', background: color }} />
      </div>
    </div>
  );
}

function PersonCard({ p, children }) {
  return (
    <div className="card" style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <strong>{p.first_name} {p.last_name}</strong>
          {p.type === 'subscription' && <span className="status-badge" style={{ marginLeft: '0.5rem' }}>Subscription</span>}
          <div style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
            {p.address ? `${p.address}, ` : ''}{p.postcode}
          </div>
          {p.phone && <div style={{ fontSize: '0.9rem' }}>📞 {p.phone}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>{children}</div>
      </div>
      {Array.isArray(p.items) && p.items.length > 0 && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-light)' }}>
          {p.items.map((it, i) => <span key={i}>{it.name} ×{it.quantity}{i < p.items.length - 1 ? ' · ' : ''}</span>)}
        </div>
      )}
      {p.special_instructions && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', fontStyle: 'italic' }}>Note: {p.special_instructions}</div>
      )}
    </div>
  );
}

function TodayTab({ flash }) {
  const [date, setDate] = useState(todayStr());
  const [schedule, setSchedule] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const [sch, st] = await Promise.all([adminAPI.getSchedule(d), adminAPI.getDashboard()]);
      setSchedule(sch.data);
      setStats(st.data);
    } catch (err) {
      flash('error', 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => { load(date); }, [date, load]);

  const setOrderStatus = async (id, status) => {
    try {
      await adminAPI.updateOrder(id, { status });
      load(date);
    } catch {
      flash('error', 'Failed to update order');
    }
  };

  if (loading || !schedule) return <div className="loading">Loading schedule…</div>;

  return (
    <div>
      {stats && (
        <div className="cards-grid" style={{ marginBottom: '1.5rem' }}>
          <StatCard label="Pickups Today" value={stats.todayPickups} />
          <StatCard label="Deliveries Today" value={stats.todayDeliveries} />
          <StatCard label="New Orders Today" value={stats.newOrdersToday} />
          <StatCard label="Revenue (This Week)" value={money(stats.revenueThisWeek)} />
          <StatCard label="Revenue (This Month)" value={money(stats.revenueThisMonth)} />
          <StatCard label="Active Subscriptions" value={stats.activeSubscriptions} />
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <label style={{ fontWeight: 600 }}>Day:</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 'auto' }} />
          <button className="btn btn-outline" onClick={() => setDate(todayStr())}>Today</button>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <CapacityBar label="Pickups" used={schedule.capacity.pickup.used} max={schedule.capacity.pickup.max} />
          <CapacityBar label="Deliveries" used={schedule.capacity.delivery.used} max={schedule.capacity.delivery.max} />
        </div>
      </div>

      <div className="schedule-columns">
        <div>
          <h2 style={{ marginBottom: '1rem' }}>📦 Pickups ({schedule.pickups.length})</h2>
          {schedule.pickups.length === 0 && <div className="card"><p>No pickups scheduled.</p></div>}
          <div style={{ display: 'grid', gap: '1rem' }}>
            {schedule.pickups.map((p) => (
              <PersonCard key={`${p.type}-${p.id}`} p={p}>
                {p.type === 'order' ? (
                  <>
                    <div style={{ fontWeight: 700 }}>{money(p.total_price)}</div>
                    <select value={p.status} onChange={(e) => setOrderStatus(p.id, e.target.value)} style={{ marginTop: '0.5rem', padding: '0.3rem' }}>
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </>
                ) : (
                  <span className="status-badge status-active">{p.frequency} · Tier {p.tier}</span>
                )}
              </PersonCard>
            ))}
          </div>
        </div>

        <div>
          <h2 style={{ marginBottom: '1rem' }}>🚚 Deliveries ({schedule.deliveries.length})</h2>
          {schedule.deliveries.length === 0 && <div className="card"><p>No deliveries scheduled.</p></div>}
          <div style={{ display: 'grid', gap: '1rem' }}>
            {schedule.deliveries.map((p) => (
              <PersonCard key={`d-${p.id}`} p={p}>
                <div style={{ fontWeight: 700 }}>{money(p.total_price)}</div>
                <select value={p.status} onChange={(e) => setOrderStatus(p.id, e.target.value)} style={{ marginTop: '0.5rem', padding: '0.3rem' }}>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </PersonCard>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="card" style={{ textAlign: 'center' }}>
      <h2 style={{ color: 'var(--primary-color)', fontSize: '2.2rem', margin: 0 }}>{value}</h2>
      <p style={{ marginTop: '0.5rem' }}>{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* New (phone-in) order                                                */
/* ------------------------------------------------------------------ */
function NewOrderTab({ flash, onCreated }) {
  const [pricing, setPricing] = useState([]);
  const [qty, setQty] = useState({});
  const [customer, setCustomer] = useState({ email: '', firstName: '', lastName: '', phone: '', address: '', postcode: '' });
  const [pickupDate, setPickupDate] = useState(todayStr());
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentStatus, setPaymentStatus] = useState('unpaid');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ordersAPI.getPricing().then((r) => setPricing(r.data)).catch(() => flash('error', 'Failed to load items'));
  }, [flash]);

  const total = pricing.reduce((sum, item) => sum + (qty[item.id] || 0) * item.price, 0);
  const setField = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const items = Object.entries(qty).filter(([, q]) => q > 0).map(([id, q]) => ({ id: Number(id), quantity: q }));
    if (items.length === 0) return flash('error', 'Add at least one item');
    setSaving(true);
    try {
      await adminAPI.createOrder({ customer, items, pickupDate, paymentMethod, paymentStatus, specialInstructions: notes, orderType: 'dry_cleaning' });
      flash('success', 'Order created');
      onCreated();
    } catch (err) {
      flash('error', err.response?.data?.error || 'Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ maxWidth: '800px' }}>
      <div className="card">
        <h3>Customer</h3>
        <div className="form-grid">
          <Field label="Email *"><input type="email" required value={customer.email} onChange={(e) => setField('email', e.target.value)} /></Field>
          <Field label="Phone"><input value={customer.phone} onChange={(e) => setField('phone', e.target.value)} /></Field>
          <Field label="First name *"><input required value={customer.firstName} onChange={(e) => setField('firstName', e.target.value)} /></Field>
          <Field label="Last name *"><input required value={customer.lastName} onChange={(e) => setField('lastName', e.target.value)} /></Field>
          <Field label="Address"><input value={customer.address} onChange={(e) => setField('address', e.target.value)} /></Field>
          <Field label="Postcode *"><input required value={customer.postcode} onChange={(e) => setField('postcode', e.target.value.toUpperCase())} /></Field>
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
          If this email already exists, the order is added to that customer; otherwise a new customer record is created.
        </p>
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h3>Items</h3>
        {pricing.map((item) => (
          <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)' }}>
            <span>{item.item_name} <span style={{ color: 'var(--text-light)' }}>· {money(item.price)}</span></span>
            <input type="number" min="0" value={qty[item.id] || 0}
              onChange={(e) => setQty((q) => ({ ...q, [item.id]: parseInt(e.target.value, 10) || 0 }))}
              style={{ width: '70px', textAlign: 'center' }} />
          </div>
        ))}
        <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '1.2rem', marginTop: '1rem' }}>Total: {money(total)}</div>
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="form-grid">
          <Field label="Pickup date *"><input type="date" required value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} /></Field>
          <Field label="Payment method">
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="cash">Cash on collection</option>
              <option value="bank">Bank transfer</option>
              <option value="card">Card</option>
            </select>
          </Field>
          <Field label="Payment status">
            <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
            </select>
          </Field>
        </div>
        <Field label="Notes"><textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      </div>

      <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: '1rem' }}>
        {saving ? 'Creating…' : `Create Order — ${money(total)}`}
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Orders                                                              */
/* ------------------------------------------------------------------ */
function OrdersTab({ flash }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await adminAPI.getOrders();
      setOrders(res.data);
    } catch {
      flash('error', 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, payload) => {
    try {
      await adminAPI.updateOrder(id, payload);
      load();
    } catch {
      flash('error', 'Update failed');
    }
  };

  if (loading) return <div className="loading">Loading orders…</div>;
  if (orders.length === 0) return <div className="card"><p>No orders yet.</p></div>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>ID</th><th>Customer</th><th>Contact</th><th>Items</th><th>Total</th>
            <th>Pickup</th><th>Delivery</th><th>Payment</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id}>
              <td>#{o.id}</td>
              <td>{o.first_name} {o.last_name}<br /><span style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{o.address}, {o.postcode}</span></td>
              <td style={{ fontSize: '0.85rem' }}>{o.email}<br />{o.phone}</td>
              <td style={{ fontSize: '0.85rem' }}>{Array.isArray(o.items) && o.items.map((it, i) => <div key={i}>{it.name} ×{it.quantity}</div>)}</td>
              <td>{money(o.total_price)}</td>
              <td>{o.pickup_date}</td>
              <td>
                <input type="date" defaultValue={o.delivery_date || ''} onBlur={(e) => e.target.value !== (o.delivery_date || '') && update(o.id, { deliveryDate: e.target.value })} style={{ width: '140px' }} />
              </td>
              <td>
                <select value={o.payment_status || 'unpaid'} onChange={(e) => update(o.id, { paymentStatus: e.target.value })} style={{ padding: '0.3rem' }}>
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                </select>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{o.payment_method || '—'}</div>
              </td>
              <td>
                <select value={o.status} onChange={(e) => update(o.id, { status: e.target.value })} style={{ padding: '0.3rem' }}>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Subscriptions / Customers (read-only tables)                        */
/* ------------------------------------------------------------------ */
function SubscriptionsTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { adminAPI.getSubscriptions().then((r) => setSubs(r.data)).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="loading">Loading…</div>;
  if (subs.length === 0) return <div className="card"><p>No subscriptions yet.</p></div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead><tr><th>ID</th><th>Customer</th><th>Contact</th><th>Type</th><th>Tier</th><th>Frequency</th><th>Next Pickup</th><th>Status</th></tr></thead>
        <tbody>
          {subs.map((s) => (
            <tr key={s.id}>
              <td>#{s.id}</td>
              <td>{s.first_name} {s.last_name}</td>
              <td style={{ fontSize: '0.85rem' }}>{s.email}<br />{s.phone}</td>
              <td>{s.subscription_type === 'laundry' ? 'Laundry' : 'Shirts & Trousers'}</td>
              <td>Tier {s.tier}</td>
              <td>{s.frequency}</td>
              <td>{s.next_pickup_date || '-'}</td>
              <td><span className={`status-badge status-${s.status}`}>{s.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { adminAPI.getCustomers().then((r) => setRows(r.data)).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="loading">Loading…</div>;
  if (rows.length === 0) return <div className="card"><p>No customers yet.</p></div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead><tr><th>ID</th><th>Name</th><th>Email</th><th>Phone</th><th>Address</th><th>Postcode</th><th>Joined</th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id}>
              <td>#{c.id}</td>
              <td>{c.first_name} {c.last_name}</td>
              <td>{c.email}</td>
              <td>{c.phone || '-'}</td>
              <td>{c.address || '-'}</td>
              <td>{c.postcode}</td>
              <td>{new Date(c.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pricing editor                                                      */
/* ------------------------------------------------------------------ */
function PricingTab({ flash }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ itemName: '', price: '', description: '' });

  const load = useCallback(async () => {
    try { setItems((await adminAPI.getPricing()).data); }
    catch { flash('error', 'Failed to load pricing'); }
    finally { setLoading(false); }
  }, [flash]);
  useEffect(() => { load(); }, [load]);

  const savePrice = async (id, price) => {
    try { await adminAPI.updatePricing(id, { price: Number(price) }); flash('success', 'Price updated'); }
    catch { flash('error', 'Update failed'); }
  };
  const toggleActive = async (item) => {
    try { await adminAPI.updatePricing(item.id, { active: !item.active }); load(); }
    catch { flash('error', 'Update failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try { await adminAPI.deletePricing(id); load(); flash('success', 'Item deleted'); }
    catch { flash('error', 'Delete failed'); }
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.addPricing({ ...newItem, price: Number(newItem.price), category: 'dry_clean' });
      setNewItem({ itemName: '', price: '', description: '' });
      load(); flash('success', 'Item added');
    } catch (err) { flash('error', err.response?.data?.error || 'Add failed'); }
  };

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div>
      <p style={{ color: 'var(--text-light)', marginBottom: '1rem' }}>Edit a price and click Save. Toggle active to hide an item from customers without deleting it.</p>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead><tr><th>Item</th><th>Price (£)</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} style={{ opacity: item.active ? 1 : 0.5 }}>
                <td>{item.item_name}<br /><span style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{item.description}</span></td>
                <td>
                  <input type="number" step="0.5" defaultValue={item.price} style={{ width: '90px' }}
                    onBlur={(e) => Number(e.target.value) !== item.price && savePrice(item.id, e.target.value)} />
                </td>
                <td><input type="checkbox" checked={!!item.active} onChange={() => toggleActive(item)} /></td>
                <td><button className="btn btn-outline" style={{ padding: '0.3rem 0.7rem' }} onClick={() => remove(item.id)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={add} className="card" style={{ marginTop: '1.5rem' }}>
        <h3>Add an item</h3>
        <div className="form-grid">
          <Field label="Name *"><input required value={newItem.itemName} onChange={(e) => setNewItem({ ...newItem, itemName: e.target.value })} /></Field>
          <Field label="Price (£) *"><input type="number" step="0.5" required value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} /></Field>
          <Field label="Description"><input value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} /></Field>
        </div>
        <button className="btn btn-primary">Add Item</button>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Service areas                                                       */
/* ------------------------------------------------------------------ */
function AreasTab({ flash }) {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newArea, setNewArea] = useState({ postcodePrefix: '', areaName: '' });

  const load = useCallback(async () => {
    try { setAreas((await adminAPI.getServiceAreas()).data); }
    catch { flash('error', 'Failed to load areas'); }
    finally { setLoading(false); }
  }, [flash]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (a) => {
    try { await adminAPI.updateServiceArea(a.id, { active: !a.active }); load(); }
    catch { flash('error', 'Update failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this area?')) return;
    try { await adminAPI.deleteServiceArea(id); load(); }
    catch { flash('error', 'Delete failed'); }
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.addServiceArea(newArea);
      setNewArea({ postcodePrefix: '', areaName: '' });
      load(); flash('success', 'Area added');
    } catch (err) { flash('error', err.response?.data?.error || 'Add failed'); }
  };

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div>
      <p style={{ color: 'var(--text-light)', marginBottom: '1rem' }}>Pause an area to temporarily stop taking bookings there (e.g. short-staffed). Prefixes are matched longest-first, so "KT13" can be more specific than "KT".</p>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead><tr><th>Prefix</th><th>Area</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {areas.map((a) => (
              <tr key={a.id} style={{ opacity: a.active ? 1 : 0.5 }}>
                <td><strong>{a.postcode_prefix}</strong></td>
                <td>{a.area_name}</td>
                <td><input type="checkbox" checked={!!a.active} onChange={() => toggle(a)} /></td>
                <td><button className="btn btn-outline" style={{ padding: '0.3rem 0.7rem' }} onClick={() => remove(a.id)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={add} className="card" style={{ marginTop: '1.5rem' }}>
        <h3>Add an area</h3>
        <div className="form-grid">
          <Field label="Postcode prefix *"><input required placeholder="e.g. KT12" value={newArea.postcodePrefix} onChange={(e) => setNewArea({ ...newArea, postcodePrefix: e.target.value.toUpperCase() })} /></Field>
          <Field label="Area name *"><input required placeholder="e.g. Walton-on-Thames" value={newArea.areaName} onChange={(e) => setNewArea({ ...newArea, areaName: e.target.value })} /></Field>
        </div>
        <button className="btn btn-primary">Add Area</button>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Settings (capacity)                                                 */
/* ------------------------------------------------------------------ */
function SettingsTab({ flash }) {
  const [settings, setSettings] = useState(null);

  useEffect(() => { adminAPI.getSettings().then((r) => setSettings(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.updateSettings({
        daily_pickup_capacity: settings.daily_pickup_capacity,
        daily_delivery_capacity: settings.daily_delivery_capacity
      });
      flash('success', 'Settings saved');
    } catch { flash('error', 'Save failed'); }
  };

  if (!settings) return <div className="loading">Loading…</div>;

  return (
    <form onSubmit={save} className="card" style={{ maxWidth: '500px' }}>
      <h3>Daily Capacity</h3>
      <p style={{ color: 'var(--text-light)', marginBottom: '1rem' }}>Used for the "almost full" warnings on the Today view.</p>
      <Field label="Max pickups per day">
        <input type="number" min="1" value={settings.daily_pickup_capacity || ''} onChange={(e) => setSettings({ ...settings, daily_pickup_capacity: e.target.value })} />
      </Field>
      <Field label="Max deliveries per day">
        <input type="number" min="1" value={settings.daily_delivery_capacity || ''} onChange={(e) => setSettings({ ...settings, daily_delivery_capacity: e.target.value })} />
      </Field>
      <button className="btn btn-primary">Save</button>
    </form>
  );
}

export default AdminDashboard;
